/**
 * Canned data for the sales demo. All data is snake_case to match
 * real API responses (the Axios response interceptor camelizes it).
 */

export const DEMO_USER = {
  pk: 999,
  first_name: "Sarah",
  last_name: "Demo",
  email: "sarah@demo.com",
  access: "demo-fake-jwt-token",
  second_step_not_required: true,
  // No staff/admin flags — the demo presents as an ordinary tenant user so no
  // internal/ops UI is exposed to prospects.
  is_staff: false,
  client_company: {
    id: 999,
    company_name: "Acme Specialty Pharmacy",
    is_admin_company: false,
  },
};

export const DEMO_CLIENT_CONFIG = {
  ui_config: {
    key: "base",
    submission_method: "cover_my_meds",
    default_post_login_page: "prior-auth",
    kanban_view_enabled: true,
    intake: {
      inbox_only: false,
      document_page_classifications: ["Medical Details", "Insurance", "Order Form", "Patient", "Other"],
      available_tags: [
        { key: "bv_pending", label: "BV Pending", color: "green", auto: false },
        { key: "bv_complete", label: "BV Complete", color: "green", auto: false },
        { key: "clinical_docs_pending", label: "Clinical Docs Pending", color: "blue", auto: false },
        { key: "urgent", label: "Urgent", color: "red", auto: false },
        { key: "on_hold", label: "On Hold", color: "grey", auto: false },
      ],
      substatus_tags: [
        { key: "authorization_status", label: "Auth", color: "blue" },
        { key: "welcome_call_status", label: "Welcome Call", color: "green" },
        { key: "servicing_branch", label: "Branch", color: "purple" },
      ],
      default_intake_view: "table",
      send_email_enabled: false,
      policy_review_enabled: false,
      reimbursement_table_enabled: false,
    },
    prior_auth: {
      cmm_intake_enabled: true,
      cmm_popup_questions: [],
      cmm_popup_answers: {},
      defaults: {},
      available_tags: [
        { key: "bv_pending", label: "BV Pending", color: "green", auto: false },
        { key: "urgent", label: "Urgent", color: "red", auto: false },
        { key: "on_hold", label: "On Hold", color: "grey", auto: false },
      ],
      substatus_tags: [
        { key: "authorization_status", label: "Auth", color: "blue" },
        { key: "welcome_call_status", label: "Welcome Call", color: "green" },
        { key: "servicing_branch", label: "Branch", color: "purple" },
      ],
    },
  },
};

const now = new Date();
const hoursAgo = (h) => new Date(now.getTime() - h * 3600000).toISOString();
const daysAgo = (d) => new Date(now.getTime() - d * 86400000).toISOString();

// --- PA Submissions (pre-seeded) ---

export const DEMO_PA_SUBMISSIONS = [
  {
    order_key: "xK7mPq2R",
    patient_first_name: "Michael",
    patient_last_name: "Rodriguez",
    substatus_tags: [
      { key: "authorization_status", value: "Approved" },
      { key: "welcome_call_status", value: "Completed" },
      { key: "servicing_branch", value: "Fresno" },
    ],
    status: "Approved",
    medication: "Humira (adalimumab)",
    insurance_provider: "Blue Cross Blue Shield",
    provider_first_name: "Dr. James",
    provider_last_name: "Wilson",
    created_on: daysAgo(12),
    modified_on: daysAgo(2),
    status_modified_on: daysAgo(2),
    form_step: "Send to Plan",
    status_tags: [],
    status_notes: [{ note: "Approved on first submission. PA valid through Oct 2026." }],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "PA-2024-88421",
      prior_auth_expiration: new Date(now.getTime() + 180 * 86400000).toISOString(),
      portal_submission_id: "BJ4F10294",
      response_documents: [{ name: "approval_letter_rodriguez.pdf", url: "https://demo-fake-s3.local/approval.pdf" }],
      response_document_url: "https://demo-fake-s3.local/approval.pdf",
      status: "Approved",
      notes: "Approved — PA# PA-2024-88421 valid 180 days",
      operational_notes: "",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Sarah Demo",
    lock: null,
  },
  {
    order_key: "bN3wLf8Y",
    substatus_tags: [
      { key: "authorization_status", value: "Pending" },
      { key: "servicing_branch", value: "Denver" },
    ],
    patient_first_name: "Jennifer",
    patient_last_name: "Chen",
    status: "Sent to plan",
    medication: "Enbrel (etanercept)",
    insurance_provider: "Aetna",
    provider_first_name: "Dr. Lisa",
    provider_last_name: "Park",
    created_on: daysAgo(5),
    modified_on: daysAgo(1),
    status_modified_on: daysAgo(1),
    form_step: "Send to Plan",
    status_tags: ["urgent"],
    status_notes: [{ note: "Expedited review requested — patient currently without medication." }],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "",
      portal_submission_id: "KR2M10301",
      response_documents: [],
      response_document_url: null,
      status: "Sent to plan",
      notes: "",
      operational_notes: "Submitted via ePA. Awaiting payer response.",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Sarah Demo",
    lock: null,
  },
  {
    order_key: "hR5tAe1D",
    substatus_tags: [
      { key: "authorization_status", value: "Denied - peer-to-peer review scheduled with Dr. Wilson on 09/14" },
      { key: "servicing_branch", value: "Sacramento" },
    ],
    patient_first_name: "Robert",
    patient_last_name: "Thompson",
    status: "Review clinical info",
    medication: "Stelara (ustekinumab)",
    insurance_provider: "UnitedHealthcare",
    provider_first_name: "Dr. Emily",
    provider_last_name: "Nguyen",
    created_on: daysAgo(3),
    modified_on: hoursAgo(6),
    status_modified_on: hoursAgo(6),
    form_step: "Review",
    status_tags: ["bv_complete"],
    status_notes: [],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "",
      portal_submission_id: "WT8N10305",
      response_documents: [],
      response_document_url: null,
      status: "",
      notes: "",
      operational_notes: "BV complete. Awaiting clinical documentation from prescriber.",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Sarah Demo",
    lock: null,
  },
  {
    order_key: "qP6vNc3F",
    patient_first_name: "David",
    patient_last_name: "Williams",
    status: "Denied",
    medication: "Xeljanz (tofacitinib)",
    insurance_provider: "Humana",
    provider_first_name: "Dr. Sarah",
    provider_last_name: "Johnson",
    created_on: daysAgo(20),
    modified_on: daysAgo(8),
    status_modified_on: daysAgo(8),
    form_step: "Send to Plan",
    status_tags: [],
    status_notes: [{ note: "Denied — step therapy not met. Appeal submitted with additional documentation." }],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "PA-2024-77103",
      portal_submission_id: "PX5Q10288",
      response_documents: [{ name: "denial_letter_williams.pdf", url: "https://demo-fake-s3.local/denial.pdf" }],
      response_document_url: "https://demo-fake-s3.local/denial.pdf",
      status: "Denied",
      notes: "Step therapy requirement not met — must trial methotrexate first",
      operational_notes: "Appeal auto-queued. Prescriber notified.",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Sarah Demo",
    lock: null,
  },
  {
    order_key: "wE2rGs7H",
    patient_first_name: "Susan",
    patient_last_name: "Anderson",
    status: "Processing",
    medication: "Rinvoq (upadacitinib)",
    insurance_provider: "Kaiser Permanente",
    provider_first_name: "Dr. Michael",
    provider_last_name: "Brown",
    created_on: hoursAgo(4),
    modified_on: hoursAgo(1),
    status_modified_on: hoursAgo(1),
    form_step: "Send to Plan",
    status_tags: [],
    status_notes: [],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "",
      portal_submission_id: "AH7L10318",
      response_documents: [],
      response_document_url: null,
      status: "",
      notes: "",
      operational_notes: "",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Sarah Demo",
    lock: null,
  },
  {
    order_key: "aT4nRk9L",
    patient_first_name: "James",
    patient_last_name: "Wilson",
    status: "Approved",
    medication: "Cosentyx (secukinumab)",
    insurance_provider: "United Healthcare",
    provider_first_name: "Dr. Amanda",
    provider_last_name: "Foster",
    created_on: daysAgo(15),
    modified_on: daysAgo(4),
    status_modified_on: daysAgo(4),
    form_step: "Send to Plan",
    status_tags: ["bv_complete"],
    status_notes: [{ note: "Approved after peer-to-peer review. PA valid 12 months." }],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "PA-2024-91003",
      prior_auth_expiration: new Date(now.getTime() + 365 * 86400000).toISOString(),
      portal_submission_id: "GV3D10271",
      response_documents: [{ name: "approval_wilson.pdf", url: "https://demo-fake-s3.local/approval_wilson.pdf" }],
      response_document_url: "https://demo-fake-s3.local/approval_wilson.pdf",
      status: "Approved",
      notes: "Approved after peer-to-peer — PA# PA-2024-91003",
      operational_notes: "",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Katherine Reyes",
    lock: null,
  },
  {
    order_key: "fG8pWm2X",
    patient_first_name: "Patricia",
    patient_last_name: "Brown",
    status: "Sent to plan",
    medication: "Skyrizi (risankizumab)",
    insurance_provider: "Anthem Blue Cross",
    provider_first_name: "Dr. Kevin",
    provider_last_name: "Patel",
    created_on: daysAgo(2),
    modified_on: hoursAgo(8),
    status_modified_on: hoursAgo(8),
    form_step: "Send to Plan",
    status_tags: [],
    status_notes: [],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "",
      portal_submission_id: "NE6C10329",
      response_documents: [],
      response_document_url: null,
      status: "",
      notes: "",
      operational_notes: "Submitted via ePA gateway. Standard processing timeline.",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Sarah Demo",
    lock: null,
  },
  {
    order_key: "kL5jBn7Q",
    patient_first_name: "Thomas",
    patient_last_name: "Davis",
    status: "Review clinical info",
    medication: "Tremfya (guselkumab)",
    insurance_provider: "Cigna",
    provider_first_name: "Dr. Rachel",
    provider_last_name: "Adams",
    created_on: daysAgo(2),
    modified_on: hoursAgo(3),
    status_modified_on: hoursAgo(3),
    form_step: "Review",
    status_tags: ["bv_pending"],
    status_notes: [{ note: "Waiting on lab results from Quest Diagnostics." }],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "",
      portal_submission_id: "YF9A10331",
      response_documents: [],
      response_document_url: null,
      status: "",
      notes: "",
      operational_notes: "BV pending — called payer, estimated 48hr turnaround.",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Katherine Reyes",
    lock: null,
  },
  {
    order_key: "uW3vDf5P",
    patient_first_name: "Richard",
    patient_last_name: "Lee",
    status: "Processing",
    medication: "Kevzara (sarilumab)",
    insurance_provider: "Aetna",
    provider_first_name: "Dr. Lisa",
    provider_last_name: "Park",
    created_on: hoursAgo(2),
    modified_on: hoursAgo(1),
    status_modified_on: hoursAgo(1),
    form_step: "Send to Plan",
    status_tags: [],
    status_notes: [],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "",
      portal_submission_id: "DM2R10335",
      response_documents: [],
      response_document_url: null,
      status: "",
      notes: "",
      operational_notes: "",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Sarah Demo",
    lock: null,
  },
  {
    order_key: "yH9sTg1M",
    patient_first_name: "Barbara",
    patient_last_name: "Taylor",
    status: "Approved",
    medication: "Cimzia (certolizumab)",
    insurance_provider: "UnitedHealthcare",
    provider_first_name: "Dr. Emily",
    provider_last_name: "Nguyen",
    created_on: daysAgo(25),
    modified_on: daysAgo(10),
    status_modified_on: daysAgo(10),
    form_step: "Send to Plan",
    status_tags: [],
    status_notes: [],
    order_submission: {
      submission_type: "CoverMyMeds",
      prior_auth_number: "PA-2024-82190",
      prior_auth_expiration: new Date(now.getTime() + 10 * 86400000).toISOString(),
      portal_submission_id: "LS4J10259",
      response_documents: [{ name: "approval_taylor.pdf", url: "https://demo-fake-s3.local/approval_taylor.pdf" }],
      response_document_url: "https://demo-fake-s3.local/approval_taylor.pdf",
      status: "Approved",
      notes: "Approved — authorization expiring soon, renewal needed",
      operational_notes: "",
    },
    can_upload_decision: false,
    staff_edit_override: false,
    modified_by_name: "Katherine Reyes",
    lock: null,
  },
];

// Template for newly imported PA — used when the rep clicks "Import CMM"
export const DEMO_IMPORT_TEMPLATE = {
  patient_first_name: "Emily",
  patient_last_name: "Martinez",
  patient_dob: "1985-03-15",
  patient_member_id: "MEM-4429817",
  status: "Review demographics info",
  medication: "Dupixent (dupilumab)",
  insurance_provider: "Anthem Blue Cross",
  insurance_plan_type: "Commercial",
  provider_first_name: "Dr. Rachel",
  provider_last_name: "Adams",
  provider_npi_number: "1234567890",
  form_step: "Review",
  status_tags: [],
  order_submission: {
    submission_type: "CoverMyMeds",
    prior_auth_number: "",
    portal_submission_id: "",
    response_documents: [],
    response_document_url: null,
    status: "",
    notes: "",
    operational_notes: "",
  },
  can_upload_decision: false,
  staff_edit_override: false,
  modified_by_name: "Sarah Demo",
  lock: null,
  // Detail-page fields
  patient_address1: "742 Evergreen Terrace",
  patient_city: "Springfield",
  patient_state: "IL",
  patient_zip: "62704",
  patient_phone: "217-555-0142",
  patient_gender: "Female",
  strength: "300 mg/2 mL",
  route_administration: "Subcutaneous",
  quantity: "2",
  days_supply: "28",
  refills: "11",
  primary_diagnosis_name: "Atopic Dermatitis",
  primary_diagnosis_icd10_code: "L20.9",
  signature: "Dr. Rachel Adams, MD",
};

// --- Intake Documents (pre-seeded) ---

export const DEMO_INTAKE_DOCUMENTS = [
  {
    key: "doc-a1b2c3",
    status: "INTAKE",
    created_on: hoursAgo(2),
    status_changed_at: hoursAgo(2),
    modified_on: hoursAgo(1),
    snooze_until: null,
    status_tags: ["bv_pending"],
    substatus_tags: [
      { key: "authorization_status", value: "Not Started" },
      { key: "welcome_call_status", value: "Scheduled" },
    ],
    patient: { first_name: "James", last_name: "Wilson", external_id: "MRN-100234" },
    patient_guess: null,
    intake_order_forms: [{ provider_first_name: "Dr. Lisa", provider_last_name: "Park", therapy_administration: null }],
    document_types: ["Referral"],
    submitted_by_name: "Front Desk",
    modified_by_name: "Sarah Demo",
    lock: null,
    automation_tasks: [],
    status_notes: [],
  },
  {
    key: "doc-d4e5f6",
    status: "INTAKE",
    created_on: hoursAgo(5),
    status_changed_at: hoursAgo(5),
    modified_on: hoursAgo(5),
    snooze_until: null,
    status_tags: [],
    patient: { first_name: "Patricia", last_name: "Brown", external_id: "MRN-100567" },
    patient_guess: null,
    intake_order_forms: [
      { provider_first_name: "Dr. Michael", provider_last_name: "Chen", therapy_administration: null },
    ],
    document_types: ["Authorization", "Insurance"],
    submitted_by_name: "Fax",
    modified_by_name: "Sarah Demo",
    lock: null,
    automation_tasks: [],
    status_notes: [],
  },
  {
    key: "doc-g7h8i9",
    status: "INTAKE_COMPLETE",
    created_on: daysAgo(3),
    status_changed_at: daysAgo(1),
    modified_on: hoursAgo(4),
    snooze_until: null,
    status_tags: ["bv_complete"],
    patient: { first_name: "Thomas", last_name: "Davis", external_id: "MRN-100891" },
    patient_guess: null,
    intake_order_forms: [
      { provider_first_name: "Dr. Emily", provider_last_name: "Nguyen", therapy_administration: null },
    ],
    document_types: ["Referral", "Clinical Documentation"],
    submitted_by_name: "Email",
    modified_by_name: "Sarah Demo",
    lock: null,
    automation_tasks: [],
    status_notes: [],
  },
];

// --- Intake Orders (pre-seeded) ---

export const DEMO_INTAKE_ORDERS = [
  {
    id: 501,
    status: "QUALIFY_PATIENT",
    created_on: hoursAgo(3),
    status_changed_at: hoursAgo(3),
    modified_on: hoursAgo(3),
    snooze_until: null,
    status_tags: [],
    patient: { first_name: "James", last_name: "Wilson", external_id: "MRN-100234", has_insurance_card: true },
    order_form: {
      provider_first_name: "Dr. Lisa",
      provider_last_name: "Park",
      therapy_type: "Humira",
      therapy_administration: [{ name: "Humira" }],
      intake_document_key: "doc-a1b2c3",
    },
    insurance_card: { payor_name: "Blue Cross Blue Shield" },
    modified_by_name: "Sarah Demo",
    status_notes: [],
  },
  {
    id: 502,
    status: "CLINICAL_REVIEW",
    created_on: daysAgo(2),
    status_changed_at: daysAgo(1),
    modified_on: hoursAgo(20),
    snooze_until: null,
    status_tags: ["urgent"],
    patient: { first_name: "Patricia", last_name: "Brown", external_id: "MRN-100567", has_insurance_card: true },
    order_form: {
      provider_first_name: "Dr. Michael",
      provider_last_name: "Chen",
      therapy_type: "Enbrel",
      therapy_administration: [{ name: "Enbrel" }],
      intake_document_key: "doc-d4e5f6",
    },
    insurance_card: { payor_name: "Aetna" },
    modified_by_name: "Sarah Demo",
    status_notes: [],
  },
];

// Prefill values for the CMM import screen in the scripted demo flow.
export const DEMO_CMM_IMPORT = {
  submission_key: "BXNQ2K3P",
  patient_last_name: "Martinez",
  patient_dob: "1985-03-15",
  extra_context_notes: "Patient has failed two prior biologics. Expedited review requested.",
  supplemental_docs: [
    {
      label: "clinical_notes_martinez.pdf",
      links: ["https://demo-fake-s3.local/clinical_notes.pdf"],
      file: null,
      uploading: false,
      error: null,
    },
  ],
};
